import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { WorkflowSectionsActions } from '../actions/workflow-sections.actions';
import { WorkflowsActions } from '../actions/workflows.actions';
import { WorkflowSectionDto } from '../dtos/workflow-section-dto.interface';

export interface WorkflowSectionsState extends EntityState<WorkflowSectionDto> {
    loading: boolean;
    error: string | null;
}

// Entity adapter
export const workflowSectionsMetadataAdapter = createEntityAdapter<WorkflowSectionDto>({
    selectId: (workflowSection) => workflowSection.id,
});

// Initial state
export const initialWorkflowSectionsState: WorkflowSectionsState = workflowSectionsMetadataAdapter.getInitialState({
    sections: [],
    loading: false,
    error: null,
});

export const workflowSectionsReducer = createReducer(
    initialWorkflowSectionsState,
    // Load sections inside workflows
    on(WorkflowsActions.workflowsLoaded, (state, { response }) => {
        const allSections = response.flatMap((wf) => wf.sections);
        return workflowSectionsMetadataAdapter.setAll(allSections, {
            ...state,
            loading: false,
            error: null,
        });
    }),

    // Load Sections
    on(WorkflowSectionsActions.loadSections, (state) => ({ ...state, loading: true })),
    on(WorkflowSectionsActions.sectionsLoaded, (state, { response }) => {
        return workflowSectionsMetadataAdapter.setAll(response, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(WorkflowSectionsActions.sectionsLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update Section
    on(WorkflowSectionsActions.updateSection, (state) => ({ ...state, loading: true })),
    on(WorkflowSectionsActions.sectionUpdated, (state, { response }) =>
        workflowSectionsMetadataAdapter.updateOne({ id: response.id, changes: response }, { ...state, loading: false }),
    ),
    on(WorkflowSectionsActions.sectionUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),
);

export const workflowSectionsFeature = createFeature({
    name: 'aumicoWorkflowSectionsFeature',
    reducer: workflowSectionsReducer,
});

export const { selectAumicoWorkflowSectionsFeatureState, selectLoading, selectError } = workflowSectionsFeature;

const { selectAll, selectEntities } = workflowSectionsMetadataAdapter.getSelectors();

export const selectAllWorkflowSections = createSelector(selectAumicoWorkflowSectionsFeatureState, selectAll);

export const selectWorkflowSectionsEntities = createSelector(selectAumicoWorkflowSectionsFeatureState, selectEntities);
